import {Injectable} from "@angular/core";
import {TreeNode} from "./dtos/interfaces/tree-node";
import {InstallType} from "./dtos/enums/install-type.enum";
import {ApplicationDto} from "./dtos/application.dto";
import {ConfigLevelType} from "./dtos/enums/config-level-type.enum";
import * as semver from "semver";
import {ConfigLevel} from "./dtos/interfaces/config-level";
import {DashboardTerminalDto} from "./dtos/dashboard-terminal.dto";
import {DeploymentDto} from "./dtos/deployment.dto";
import {UtilitiesService} from "./utilities.service";

@Injectable()
export class DeploymentUtilitiesService {
    private _lowestServicesVersionSupported = semver.valid(semver.coerce("3.41.0+1000"));

    constructor(private utilitiesService: UtilitiesService) {
    }

    isConfigLevel(node: TreeNode): node is ConfigLevel {
        return (<ConfigLevel>node).name !== undefined;
    }

    nodeAsLevel(node: TreeNode): ConfigLevel {
        return node as ConfigLevel;
    }

    nodeAsApplication(node: TreeNode): ApplicationDto {
        return node as ApplicationDto;
    }

    nodeAsTerminal(node: TreeNode): DashboardTerminalDto {
        return node as DashboardTerminalDto;
    }

    getNodeName(node: TreeNode) : string {
        if (this.isConfigLevel(node)) {
            let level = this.nodeAsLevel(node);
            return !this.utilitiesService.isEmptyOrSpaces(level.getDisplayName()) ? level.getDisplayName() : `(Unnamed ${ConfigLevelType[level.configLevel]})`;
        }

        return this.utilitiesService.getApplicationName(this.nodeAsApplication(node).application);
    }

    getDeploymentsForNode(node: TreeNode, deployments: DeploymentDto[], targetVersion: string) : DeploymentDto[] {
        let applications = this.getApplicationsForNode(node, true, null, targetVersion);
        return deployments.filter(x => applications.some(y => x.application == y.application && x.terminalId == y.terminalId));
    }

    // note: applications don't know if they can use cloud deployments or not, so canUseCloudDeploymentsOnly has no impact at application level
    getApplicationsForNode(node: TreeNode, canUseCloudDeploymentsOnly: boolean = false, installType: InstallType = null, targetVersion: string = null): ApplicationDto[] {
        if (node.configLevel == ConfigLevelType.Application) {
            if (installType != null)
                if (!this.applicationIsInstallType(node, installType, targetVersion)) {
                    return [];
                }

            return [this.nodeAsApplication(node)];
        }

        if (node.configLevel == null || (canUseCloudDeploymentsOnly && this.isConfigLevel(node) && !this.nodeAsLevel(node).canUseCloudDeployments)) {
            return [];
        }

        let applications: ApplicationDto[] = [];
        for (let child of node.getChildren()) {
            applications.push(...this.getApplicationsForNode(child, canUseCloudDeploymentsOnly, installType, targetVersion));
        }

        return applications;
    }

    applicationIsInstallType(applicationNode: TreeNode, installType: InstallType, targetVersion: string) : boolean {
        let version = semver.valid(semver.coerce(targetVersion));
        if (version == null || applicationNode == null || this.isConfigLevel(applicationNode)) return false;

        let application = this.nodeAsApplication(applicationNode);

        switch (installType) {
            case InstallType.Upgrade: {
                return semver.lt(semver.coerce(application.version), version);
            }
            case InstallType.Reinstall: {
                return semver.eq(semver.coerce(application.version), version);
            }
            case InstallType.Downgrade: {
                return semver.gt(semver.coerce(application.version), version);
            }
            default: {
                return false;
            }
        }
    }

    canUseCloudDeployments(servicesVersion: string) : boolean {
        let parsedServicesVersion = semver.valid(semver.coerce(servicesVersion));
        if (parsedServicesVersion == null || this._lowestServicesVersionSupported == null) return false;

        return semver.gt(parsedServicesVersion, this._lowestServicesVersionSupported);
    }
}


// WEBPACK FOOTER //
// ./~/angular2-template-loader!./src/app/services/deployment-utilities.service.ts